import React, { useCallback, useEffect, useId, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';

/**
 * Socle de modale (passation design §5).
 *
 * - Cinq largeurs, pas douze : sm 400 (confirmation), md 520 (formulaire court, sélecteur),
 *   lg 640 (formulaire à sections, détail en lecture), xl 780 (deux colonnes),
 *   2xl 960 (visionneuse de document). Au-delà, c'est une page.
 * - Le voile n'est jamais opaque : on doit voir d'où l'on vient.
 * - Le corps scrolle (85vh max), jamais la page ; en-tête et pied restent visibles.
 * - Échap ferme, Tab tourne en boucle, le focus revient au bouton d'origine à la fermeture.
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE_PX: Record<ModalSize, number> = { sm: 400, md: 520, lg: 640, xl: 780, '2xl': 960 };

// Anciens appels `maxWidth="max-w-lg"` : traduits vers la largeur de socle la plus proche.
const LEGACY_CLASS_TO_SIZE: Record<string, ModalSize> = {
    'max-w-xs': 'sm', 'max-w-sm': 'sm',
    'max-w-md': 'md',
    'max-w-lg': 'lg', 'max-w-xl': 'lg',
    'max-w-2xl': 'xl', 'max-w-3xl': 'xl',
    'max-w-4xl': '2xl', 'max-w-5xl': '2xl', 'max-w-6xl': '2xl',
};

export interface ModalProps {
    open: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    /** Une ligne sous le titre qui dit ce que l'action va faire. */
    subtitle?: React.ReactNode;
    icon?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: ModalSize;
    /** @deprecated préférer `size`. Classe Tailwind `max-w-*`, traduite vers `size`. */
    maxWidth?: string;
    zIndex?: number;
    /** Fermer en cliquant le voile (oui par défaut). */
    closeOnOverlay?: boolean;
    /** Retire le rembourrage du corps (visionneuse de document, tableau pleine largeur). */
    flush?: boolean;
    /** Sans en-tête ni pied, le contenu gère tout (rare : visionneuse plein cadre). */
    bare?: boolean;
    /** Pied : les actions sont à droite par défaut ; `split` met la suppression à gauche. */
    footerLayout?: 'end' | 'split';
    /**
     * Largeur exacte en pixels, quand une maquette en fixe une hors de l'échelle
     * ci-dessus (ex. les 920 px des paramètres du planning). À n'utiliser que dans
     * ce cas : partout ailleurs, `size` reste la règle.
     */
    widthPx?: number;
    /**
     * Panneau latéral ancré à droite plutôt que modale centrée (patron « fiche-dossier »,
     * passation §8 C) : pleine hauteur, il glisse depuis le bord au lieu de monter.
     *
     * Tout le reste est identique — mêmes largeurs, même voile translucide, Échap, focus piégé,
     * en-tête et pied fixes avec le corps qui scrolle entre les deux. Sur téléphone, il prend
     * toute la largeur : un panneau de 640 px n'aurait aucun sens sur 375 px.
     *
     * Option, jamais le défaut : les modales existantes ne bougent pas.
     */
    side?: boolean;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
// Même liste, restreinte au corps : le premier champ du formulaire, pas le bouton Fermer de l'en-tête.
const BODY_FOCUSABLE = FOCUSABLE.split(', ').map((sel) => `.modal-body ${sel}`).join(', ');

// Dernier élément focalisé HORS d'une modale. Lu au moment de l'ouverture : un champ portant
// autoFocus a déjà pris le focus quand l'effet de la modale s'exécute, on ne peut donc pas se
// fier à document.activeElement à ce moment-là.
let lastOuterFocus: HTMLElement | null = null;
if (typeof document !== 'undefined') {
    document.addEventListener('focusin', (e) => {
        const el = e.target as HTMLElement | null;
        if (el && !el.closest('[role="dialog"]')) lastOuterFocus = el;
    }, true);
}

export const Modal: React.FC<ModalProps> = ({
    open,
    onClose,
    title,
    subtitle,
    icon,
    children,
    footer,
    size,
    maxWidth,
    zIndex = 70,
    closeOnOverlay = true,
    flush = false,
    bare = false,
    footerLayout = 'end',
    widthPx: widthOverride,
    side = false,
}) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const titleId = useId();
    const resolvedSize: ModalSize = size || (maxWidth && LEGACY_CLASS_TO_SIZE[maxWidth]) || 'md';
    const widthPx = widthOverride ?? SIZE_PX[resolvedSize];

    // Focus : mémoriser l'élément d'origine, entrer dans la modale, revenir à la fermeture.
    useEffect(() => {
        if (!open) return;
        const current = document.activeElement as HTMLElement | null;
        openerRef.current = current && !current.closest('[role="dialog"]') ? current : lastOuterFocus;
        const raf = requestAnimationFrame(() => {
            const panel = panelRef.current;
            if (!panel) return;
            const preferred = panel.querySelector<HTMLElement>('[data-autofocus]');
            const first = preferred || panel.querySelector<HTMLElement>(BODY_FOCUSABLE) || panel.querySelector<HTMLElement>(FOCUSABLE);
            (first || panel).focus({ preventScroll: true });
        });
        return () => {
            cancelAnimationFrame(raf);
            const opener = openerRef.current;
            if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
                opener.focus({ preventScroll: true });
            }
        };
    }, [open]);

    // Le corps de la modale scrolle, jamais la page.
    useEffect(() => {
        if (!open) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [open]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
            return;
        }
        if (e.key !== 'Tab' || !panelRef.current) return;
        const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
            .filter((el) => el.offsetParent !== null);
        if (nodes.length === 0) { e.preventDefault(); return; }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
            e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault(); first.focus();
        }
    }, [onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className={side
                        ? 'fixed inset-0 flex items-stretch justify-end'
                        : 'fixed inset-0 flex items-center justify-center p-4 sm:p-6'}
                    style={{ zIndex, background: 'var(--modal-veil)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    onMouseDown={(e) => { if (closeOnOverlay && e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        ref={panelRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={title !== undefined ? titleId : undefined}
                        tabIndex={-1}
                        initial={side ? { opacity: 0, x: 24 } : { opacity: 0, y: 10, scale: 0.985 }}
                        animate={side ? { opacity: 1, x: 0 } : { opacity: 1, y: 0, scale: 1 }}
                        exit={side ? { opacity: 0, x: 24 } : { opacity: 0, y: 6, scale: 0.985 }}
                        transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}
                        className={side
                            ? 'bg-surface border-l border-border w-full h-full shadow-2xl overflow-hidden flex flex-col outline-none'
                            : 'bg-surface border border-border rounded-2xl w-full max-h-[85vh] shadow-2xl overflow-hidden flex flex-col outline-none'}
                        style={{ maxWidth: widthPx }}
                        onKeyDown={handleKeyDown}
                    >
                        {!bare && title !== undefined && (
                            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border shrink-0">
                                <div className="min-w-0">
                                    <h2 id={titleId} className="text-[15.5px] font-semibold text-content flex items-center gap-2 leading-tight">
                                        {icon}
                                        <span className="truncate">{title}</span>
                                    </h2>
                                    {subtitle !== undefined && (
                                        <p className="text-[12.5px] text-content-muted mt-0.5">{subtitle}</p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label="Fermer"
                                    className="w-[30px] h-[30px] shrink-0 -mr-1 -mt-0.5 flex items-center justify-center text-content-muted hover:text-content rounded-lg hover:bg-surface-soft transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        )}

                        <div className={`modal-body overflow-y-auto flex-1 min-h-0 ${flush || bare ? '' : 'p-5'}`}>{children}</div>

                        {!bare && footer !== undefined && (
                            <div className={`flex flex-wrap items-center gap-2 px-5 py-3.5 border-t border-border bg-surface-soft shrink-0 ${footerLayout === 'split' ? 'justify-between' : 'justify-end'}`}>
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
